import React, { useState } from 'react';
import {
  Box,
  Typography,
  Button,
  Paper,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  Stack,
  Slider,
  FormControlLabel,
  Switch,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Grid,
  Chip,
  IconButton,
  Tooltip,
  Card,
  CardContent
} from '@mui/material';
import {
  ExpandMore as ExpandMoreIcon,
  Add as AddIcon,
  PlayArrow as PlayIcon,
  Pause as PauseIcon,
  Timeline as TimelineIcon,
  FilterList as FilterIcon,
  Save as SaveIcon,
  Compare as CompareIcon
} from '@mui/icons-material';
import { MapContainer, TileLayer, ZoomControl, ScaleControl } from 'react-leaflet';
import { SelectChangeEvent } from '@mui/material/Select';
import { filterPanelStyles } from '../../styles/map/filterPanelStyles';
import FilterToggleButton from '../../components/map/FilterToggleButton';
import MapFilters from './MapFilters';

// Types for scenario data
interface Scenario {
  id: string;
  name: string;
  type: string;
  parameters: ScenarioParameters;
  status: 'draft' | 'active' | 'completed';
}

interface ScenarioParameters {
  timeframe: {
    lookbackPeriod: number;
    startDate: Date;
    completionDate: Date;
    projectLife: number;
  };
  budget: {
    min: number;
    max: number;
  };
  metrics: {
    netPatientRevenue: number;
    marketShare: number;
    patientLeakage: number;
    waitTimes: number;
    readmissionRates: number;
    careScores: number;
    expenses: number;
    irr: number;
    npv: number;
    paybackPeriod: number;
    baselineCosts: number;
    coveredLives: number;
  };
  filters: {
    facilities: string[];
    providers: string[];
    geography: {
      type: string;
      value: string;
    };
    specialties: string[];
    patientCohorts: string[];
    organizations: string[];
  };
}

// Add scenario type definitions
const SCENARIO_TYPES = {
  'Market Expansion': {
    metrics: {
      netPatientRevenue: { label: 'Target Revenue Growth (%)', min: -50, max: 100 },
      marketShare: { label: 'Target Market Share (%)', min: 0, max: 100 },
      patientLeakage: { label: 'Expected Leakage Reduction (%)', min: -50, max: 0 },
      waitTimes: { label: 'Target Wait Time Reduction (%)', min: -50, max: 0 },
      careScores: { label: 'Target Care Score Improvement (%)', min: 0, max: 50 },
      expenses: { label: 'Expected Cost Increase (%)', min: 0, max: 100 },
      irr: { label: 'Target IRR (%)', min: 0, max: 50 },
      npv: { label: 'Target NPV ($)', min: 0, max: 10000000 },
      paybackPeriod: { label: 'Target Payback Period (months)', min: 1, max: 60 },
      coveredLives: { label: 'Target Covered Lives Increase (%)', min: 0, max: 100 }
    }
  },
  'Leakage Reduction': {
    metrics: {
      netPatientRevenue: { label: 'Revenue Recovery (%)', min: 0, max: 100 },
      patientLeakage: { label: 'Leakage Reduction Target (%)', min: -50, max: 0 },
      waitTimes: { label: 'Wait Time Improvement (%)', min: -50, max: 0 },
      readmissionRates: { label: 'Readmission Rate Reduction (%)', min: -50, max: 0 },
      careScores: { label: 'Care Score Improvement (%)', min: 0, max: 50 },
      expenses: { label: 'Cost Reduction (%)', min: -50, max: 0 },
      coveredLives: { label: 'Patient Retention Increase (%)', min: 0, max: 100 }
    }
  },
  'Service Relocation': {
    metrics: {
      netPatientRevenue: { label: 'Revenue Impact (%)', min: -50, max: 100 },
      marketShare: { label: 'Market Share Impact (%)', min: -50, max: 100 },
      patientLeakage: { label: 'Leakage Impact (%)', min: -50, max: 50 },
      waitTimes: { label: 'Wait Time Impact (%)', min: -50, max: 50 },
      expenses: { label: 'Cost Impact (%)', min: -50, max: 50 },
      irr: { label: 'Target IRR (%)', min: 0, max: 50 },
      npv: { label: 'Target NPV ($)', min: -10000000, max: 10000000 },
      paybackPeriod: { label: 'Target Payback Period (months)', min: 1, max: 60 }
    }
  },
  'Hiring': {
    metrics: {
      netPatientRevenue: { label: 'Revenue Growth (%)', min: 0, max: 100 },
      marketShare: { label: 'Market Share Growth (%)', min: 0, max: 100 },
      patientLeakage: { label: 'Leakage Reduction (%)', min: -50, max: 0 },
      waitTimes: { label: 'Wait Time Reduction (%)', min: -50, max: 0 },
      careScores: { label: 'Care Score Improvement (%)', min: 0, max: 50 },
      expenses: { label: 'Cost Increase (%)', min: 0, max: 100 },
      irr: { label: 'Target IRR (%)', min: 0, max: 50 },
      npv: { label: 'Target NPV ($)', min: 0, max: 10000000 },
      paybackPeriod: { label: 'Target Payback Period (months)', min: 1, max: 60 }
    }
  },
  'M&A': {
    metrics: {
      netPatientRevenue: { label: 'Combined Revenue Growth (%)', min: 0, max: 100 },
      marketShare: { label: 'Combined Market Share (%)', min: 0, max: 100 },
      patientLeakage: { label: 'Leakage Reduction (%)', min: -50, max: 0 },
      expenses: { label: 'Cost Synergy (%)', min: -50, max: 0 },
      irr: { label: 'Target IRR (%)', min: 0, max: 50 },
      npv: { label: 'Target NPV ($)', min: 0, max: 10000000 },
      paybackPeriod: { label: 'Target Payback Period (months)', min: 1, max: 60 }
    }
  },
  'Payor Network': {
    metrics: {
      netPatientRevenue: { label: 'Revenue Impact (%)', min: -50, max: 100 },
      marketShare: { label: 'Market Share Impact (%)', min: -50, max: 100 },
      patientLeakage: { label: 'Leakage Impact (%)', min: -50, max: 50 },
      expenses: { label: 'Cost Impact (%)', min: -50, max: 50 },
      coveredLives: { label: 'Covered Lives Change (%)', min: -50, max: 100 }
    }
  },
  'Patient Marketing': {
    metrics: {
      netPatientRevenue: { label: 'Revenue Growth (%)', min: 0, max: 100 },
      marketShare: { label: 'Market Share Growth (%)', min: 0, max: 100 },
      patientLeakage: { label: 'Leakage Reduction (%)', min: -50, max: 0 },
      careScores: { label: 'Care Score Improvement (%)', min: 0, max: 50 },
      expenses: { label: 'Marketing Cost (%)', min: 0, max: 100 },
      coveredLives: { label: 'New Patient Acquisition (%)', min: 0, max: 100 }
    }
  },
  'Operations': {
    metrics: {
      netPatientRevenue: { label: 'Revenue Impact (%)', min: -50, max: 100 },
      waitTimes: { label: 'Wait Time Reduction (%)', min: -50, max: 0 },
      readmissionRates: { label: 'Readmission Rate Reduction (%)', min: -50, max: 0 },
      careScores: { label: 'Care Score Improvement (%)', min: 0, max: 50 },
      expenses: { label: 'Cost Reduction (%)', min: -50, max: 0 },
      irr: { label: 'Target IRR (%)', min: 0, max: 50 },
      npv: { label: 'Target NPV ($)', min: 0, max: 10000000 },
      paybackPeriod: { label: 'Target Payback Period (months)', min: 1, max: 60 }
    }
  },
  'Competitive': {
    metrics: {
      netPatientRevenue: { label: 'Revenue Impact (%)', min: -50, max: 100 },
      marketShare: { label: 'Market Share Impact (%)', min: -50, max: 100 },
      patientLeakage: { label: 'Leakage Impact (%)', min: -50, max: 50 },
      careScores: { label: 'Care Score Impact (%)', min: -50, max: 50 },
      expenses: { label: 'Cost Impact (%)', min: -50, max: 50 }
    }
  },
  'Comprehensive': {
    metrics: {
      netPatientRevenue: { label: 'Overall Revenue Impact (%)', min: -50, max: 100 },
      marketShare: { label: 'Market Share Impact (%)', min: -50, max: 100 },
      patientLeakage: { label: 'Leakage Impact (%)', min: -50, max: 50 },
      waitTimes: { label: 'Wait Time Impact (%)', min: -50, max: 50 },
      readmissionRates: { label: 'Readmission Rate Impact (%)', min: -50, max: 50 },
      careScores: { label: 'Care Score Impact (%)', min: -50, max: 50 },
      expenses: { label: 'Cost Impact (%)', min: -50, max: 50 },
      irr: { label: 'Target IRR (%)', min: 0, max: 50 },
      npv: { label: 'Target NPV ($)', min: -10000000, max: 10000000 },
      paybackPeriod: { label: 'Target Payback Period (months)', min: 1, max: 60 },
      coveredLives: { label: 'Covered Lives Impact (%)', min: -50, max: 100 }
    }
  }
} as const;

const ScenarioBuilder: React.FC = () => {
  const [scenarios, setScenarios] = useState<Scenario[]>([
    {
      id: '1',
      name: 'Market Expansion - Cardiac Care',
      type: 'Market Expansion',
      status: 'draft',
      parameters: {
        timeframe: {
          lookbackPeriod: 12,
          startDate: new Date(),
          completionDate: new Date(Date.now() + 180 * 24 * 60 * 60 * 1000),
          projectLife: 60
        },
        budget: {
          min: 1000000,
          max: 5000000
        },
        metrics: {
          netPatientRevenue: 15,
          marketShare: 25,
          patientLeakage: -10,
          waitTimes: -20,
          readmissionRates: -5,
          careScores: 10,
          expenses: -15,
          irr: 20,
          npv: 3000000,
          paybackPeriod: 36,
          baselineCosts: -10,
          coveredLives: 15
        },
        filters: {
          facilities: [],
          providers: [],
          geography: {
            type: 'zip',
            value: ''
          },
          specialties: ['Cardiology'],
          patientCohorts: [],
          organizations: []
        }
      }
    }
  ]);
  const [selectedScenario, setSelectedScenario] = useState<Scenario>(scenarios[0]);
  const [isPlaying, setIsPlaying] = useState(false);
  const [showFilters, setShowFilters] = useState(true);

  const handleScenarioTypeChange = (event: SelectChangeEvent<string>) => {
    setSelectedScenario({
      ...selectedScenario,
      type: event.target.value
    });
  };

  const handleMetricChange = (metric: keyof ScenarioParameters['metrics'], value: number) => {
    setSelectedScenario({
      ...selectedScenario,
      parameters: {
        ...selectedScenario.parameters,
        metrics: {
          ...selectedScenario.parameters.metrics,
          [metric]: value
        }
      }
    });
  };

  const togglePlayPause = () => {
    setIsPlaying(!isPlaying);
  };

  const handleApplyFilters = (filters: any) => {
    // Handle filter application
    console.log('Applied filters:', filters);
  };

  const renderScenarioControls = () => (
    <Box sx={{ mb: 2 }}>
      <Stack direction="row" spacing={1} alignItems="center">
        <FormControl size="small" sx={{ minWidth: 200 }}>
          <InputLabel>Scenario Type</InputLabel>
          <Select
            value={selectedScenario.type}
            onChange={handleScenarioTypeChange}
            label="Scenario Type"
          >
            <MenuItem value="Market Expansion">Market Expansion</MenuItem>
            <MenuItem value="Leakage Reduction">Leakage Reduction</MenuItem>
            <MenuItem value="Service Relocation">Service/Site Relocation</MenuItem>
            <MenuItem value="Hiring">Hiring/Recruitment</MenuItem>
            <MenuItem value="M&A">M&A</MenuItem>
            <MenuItem value="Payor Network">Payor Network Optimization</MenuItem>
            <MenuItem value="Patient Marketing">Patient Marketing</MenuItem>
            <MenuItem value="Operations">Operations Optimization</MenuItem>
            <MenuItem value="Competitive">Competitive Response</MenuItem>
            <MenuItem value="Comprehensive">Comprehensive Strategy</MenuItem>
          </Select>
        </FormControl>
        <Tooltip title="Save current scenario">
          <IconButton 
            color="primary"
            size="small"
            sx={{ 
              bgcolor: 'primary.main',
              color: 'white',
              '&:hover': {
                bgcolor: 'primary.dark',
              }
            }}
          >
            <SaveIcon fontSize="small" />
          </IconButton>
        </Tooltip>
        <Tooltip title="Compare scenarios">
          <IconButton 
            color="primary"
            size="small"
            sx={{ 
              border: 1,
              borderColor: 'primary.main',
              '&:hover': {
                bgcolor: 'action.hover',
              }
            }}
          >
            <CompareIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      </Stack>
    </Box>
  );

  const renderTimeframeControls = () => (
    <Accordion defaultExpanded>
      <AccordionSummary 
        expandIcon={<ExpandMoreIcon />}
        sx={{
          minHeight: 56,
          '& .MuiAccordionSummary-content': {
            m: 0
          }
        }}
      >
        <Typography 
          sx={{ 
            display: 'flex', 
            alignItems: 'center', 
            gap: 1,
            fontWeight: 500,
            fontSize: '0.875rem'
          }}
        >
          <TimelineIcon fontSize="small" color="primary" />
          Timeframe Parameters
        </Typography>
      </AccordionSummary>
      <AccordionDetails sx={{ p: 2, pt: 0 }}>
        <Stack spacing={3}>
          <Box>
            <Typography variant="subtitle2" gutterBottom sx={{ fontSize: '0.75rem', color: 'text.secondary' }}>
              Lookback Period (months)
            </Typography>
            <Slider
              value={selectedScenario.parameters.timeframe.lookbackPeriod}
              onChange={(_, value) => setSelectedScenario({
                ...selectedScenario,
                parameters: {
                  ...selectedScenario.parameters,
                  timeframe: {
                    ...selectedScenario.parameters.timeframe,
                    lookbackPeriod: value as number
                  }
                }
              })}
              min={1}
              max={60}
              valueLabelDisplay="auto"
            />
          </Box>
          <Box>
            <Typography variant="subtitle2" gutterBottom sx={{ fontSize: '0.75rem', color: 'text.secondary' }}>
              Project Life (months)
            </Typography>
            <Slider
              value={selectedScenario.parameters.timeframe.projectLife}
              onChange={(_, value) => setSelectedScenario({
                ...selectedScenario,
                parameters: {
                  ...selectedScenario.parameters,
                  timeframe: {
                    ...selectedScenario.parameters.timeframe,
                    projectLife: value as number
                  }
                }
              })}
              min={1}
              max={120}
              valueLabelDisplay="auto"
            />
          </Box>
        </Stack>
      </AccordionDetails>
    </Accordion>
  );

  const renderBudgetControls = () => (
    <Accordion defaultExpanded>
      <AccordionSummary 
        expandIcon={<ExpandMoreIcon />}
        sx={{
          minHeight: 56,
          '& .MuiAccordionSummary-content': {
            m: 0
          }
        }}
      >
        <Typography 
          sx={{ 
            display: 'flex', 
            alignItems: 'center', 
            gap: 1,
            fontWeight: 500,
            fontSize: '0.875rem'
          }}
        >
          <FilterIcon fontSize="small" color="primary" />
          Budget Parameters
        </Typography>
      </AccordionSummary>
      <AccordionDetails sx={{ p: 2, pt: 0 }}>
        <Stack spacing={3}>
          <Box>
            <Typography variant="subtitle2" gutterBottom sx={{ fontSize: '0.75rem', color: 'text.secondary' }}>
              Budget Range ($)
            </Typography>
            <Slider
              value={[selectedScenario.parameters.budget.min, selectedScenario.parameters.budget.max]}
              onChange={(_, value) => setSelectedScenario({
                ...selectedScenario,
                parameters: {
                  ...selectedScenario.parameters,
                  budget: {
                    min: (value as number[])[0],
                    max: (value as number[])[1]
                  }
                }
              })}
              min={0}
              max={10000000}
              valueLabelDisplay="auto"
              valueLabelFormat={(value) => `$${value.toLocaleString()}`}
            />
          </Box>
        </Stack>
      </AccordionDetails>
    </Accordion>
  );

  const renderMetricsControls = () => {
    const scenarioMetrics = SCENARIO_TYPES[selectedScenario.type as keyof typeof SCENARIO_TYPES]?.metrics || {};
    
    return (
      <Accordion defaultExpanded>
        <AccordionSummary 
          expandIcon={<ExpandMoreIcon />}
          sx={{
            minHeight: 56,
            '& .MuiAccordionSummary-content': {
              m: 0
            }
          }}
        >
          <Typography 
            sx={{ 
              display: 'flex', 
              alignItems: 'center', 
              gap: 1,
              fontWeight: 500,
              fontSize: '0.875rem'
            }}
          >
            <FilterIcon fontSize="small" color="primary" />
            Key Evaluation Metrics
          </Typography>
        </AccordionSummary>
        <AccordionDetails sx={{ p: 2, pt: 0 }}>
          <Stack spacing={3}>
            {Object.entries(scenarioMetrics).map(([key, config]) => (
              <Box key={key}>
                <Typography variant="subtitle2" gutterBottom sx={{ fontSize: '0.75rem', color: 'text.secondary' }}>
                  {config.label}
                </Typography>
                <Slider
                  value={selectedScenario.parameters.metrics[key as keyof ScenarioParameters['metrics']]}
                  onChange={(_, value) => handleMetricChange(key as keyof ScenarioParameters['metrics'], value as number)}
                  min={config.min}
                  max={config.max}
                  valueLabelDisplay="auto"
                  valueLabelFormat={(value) => 
                    key === 'npv' ? `$${value.toLocaleString()}` : `${value}%`
                  }
                />
              </Box>
            ))}
          </Stack>
        </AccordionDetails>
      </Accordion>
    );
  };

  const renderMapControls = () => (
    <Box sx={{ mt: 2 }}>
      <Stack direction="row" spacing={1} alignItems="center">
        <FormControlLabel
          control={<Switch size="small" />}
          label="Show Heatmap"
        />
        <Tooltip title="Play/Pause Simulation">
          <IconButton size="small" onClick={togglePlayPause}>
            {isPlaying ? <PauseIcon /> : <PlayIcon />}
          </IconButton>
        </Tooltip>
        <Tooltip title="Timeline">
          <IconButton size="small">
            <TimelineIcon />
          </IconButton>
        </Tooltip>
      </Stack>
    </Box>
  );

  return (
    <Box sx={{ height: 'calc(100vh - 240px)', display: 'flex', flexDirection: 'column' }}>
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
        <Box>
          <Typography variant="h6" gutterBottom>Scenario Builder</Typography>
          <Typography variant="body2" color="text.secondary">
            Create and analyze different network scenarios
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <FilterToggleButton showFilters={showFilters} onToggle={() => setShowFilters(!showFilters)} />
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            size="small"
          >
            New Scenario
          </Button>
        </Box>
      </Box>

      {/* Main Content */}
      <Box sx={{ 
        flexGrow: 1,
        minHeight: 0,
        display: 'flex',
        gap: 0
      }}>
        {/* Filters Panel */}
        <Paper sx={filterPanelStyles.panel({ showFilters })}>
          {showFilters && (
            <Box sx={filterPanelStyles.container}>
              <Box sx={filterPanelStyles.content}>
                <Card 
                  elevation={0} 
                  sx={{ 
                    borderRadius: 0,
                    borderBottom: '1px solid',
                    borderColor: 'divider'
                  }}
                >
                  <CardContent sx={{ py: 2 }}>
                    <Typography 
                      variant="h6" 
                      sx={{ 
                        display: 'flex', 
                        alignItems: 'center', 
                        gap: 1,
                        fontSize: '1.1rem',
                        fontWeight: 500
                      }}
                    >
                      <FilterIcon fontSize="small" /> Scenario Filters
                    </Typography>
                  </CardContent>
                </Card>

                <Box sx={{ 
                  flex: 1, 
                  overflowY: 'auto',
                  p: 2
                }}>
                  {renderScenarioControls()}
                  {renderTimeframeControls()}
                  {renderBudgetControls()}
                  {renderMetricsControls()}
                </Box>
              </Box>
            </Box>
          )}
        </Paper>

        {/* Map Container */}
        <Paper 
          elevation={3} 
          sx={{ 
            flexGrow: 1,
            minHeight: 0,
            display: 'flex',
            flexDirection: 'column',
            overflow: 'hidden',
            borderRadius: showFilters ? '0 4px 4px 0' : '4px'
          }}
        >
          <Box sx={{ p: 1 }}>
            {renderMapControls()}
          </Box>
          <Box sx={{ flexGrow: 1, position: 'relative' }}>
            <MapContainer
              center={[28.5383, -81.3792]}
              zoom={10}
              scrollWheelZoom={true}
              zoomControl={false}
              style={{ height: '100%', width: '100%' }}
            >
              <TileLayer
                attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
                url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
              />
              <ZoomControl position="topright" />
              <ScaleControl position="bottomright" />
            </MapContainer>
          </Box>
        </Paper>
      </Box>
    </Box>
  );
};

export default ScenarioBuilder; 